import { TabContext, TabPanel } from '@mui/lab';
import { Box } from '@mui/material';
import {
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import Snackbar from '../../components/Snackbar';
import { TitleBox } from '../../components/UI/Box';
import { GridMain } from '../../components/UI/Grid';
import { TitleTypography } from '../../components/UI/Typography';
import { IconHubMS } from '../../constants/icons';
import { GlobalContext } from '../../context/global';
import { AccessKey, canSee } from '../../helpers/roleAccess';
import { useCurrentRole } from '../../hooks/useCurrentRole';
import { StyledTabList, TabStyled } from './styles';

interface ITabValues {
  value: 'requests' | 'licensed' | 'notices' | 'rede-proto';
}

const HIVE_TABS: {
  label: string;
  value: ITabValues['value'];
  key: AccessKey;
}[] = [
  { label: 'Pedidos', value: 'requests', key: 'hivePedidos' },
  { label: 'Credenciados', value: 'licensed', key: 'hiveCredenciados' },
  { label: 'Editais', value: 'notices', key: 'hiveEditais' },
  {
    label: 'Dashboard credenciado',
    value: 'rede-proto',
    key: 'hiveDashboard',
  },
];

export function Hive(): JSX.Element {
  const [value, setValue] = useState<ITabValues['value']>();

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { openSnackbar } = useContext(GlobalContext);
  const role = useCurrentRole();

  const visibleTabs = useMemo(
    () => HIVE_TABS.filter((tab) => canSee(tab.key, role)),
    [role]
  );

  useEffect(() => {
    let intended: ITabValues['value'];
    if (pathname.includes('rede-proto')) {
      intended = 'rede-proto';
    } else if (pathname.includes('licensed')) {
      intended = 'licensed';
    } else if (pathname.includes('notices')) {
      intended = 'notices';
    } else {
      intended = 'requests';
    }

    // Cai na primeira aba visível quando a rota atual não é permitida ao papel
    // (ex.: CS/financeiro entrando no índice, que aponta para "Pedidos").
    if (visibleTabs.some((tab) => tab.value === intended)) {
      setValue(intended);
    } else if (visibleTabs.length > 0) {
      setValue(visibleTabs[0].value);
      navigate(`./${visibleTabs[0].value}`);
    }
  }, [pathname, visibleTabs, navigate]);

  const handleChangeTab = (
    event: SyntheticEvent,
    newValue: ITabValues['value']
  ): void => {
    setValue(newValue);
    navigate(`./${newValue}`);
  };

  return (
    <GridMain>
      <TitleBox>
        <TitleTypography>{IconHubMS} The Hive</TitleTypography>
      </TitleBox>
      <Box>
        {value && (
          <TabContext value={value}>
            <Box sx={{ borderBottom: 1, borderColor: '#707070' }}>
              <StyledTabList onChange={handleChangeTab}>
                {visibleTabs.map((tab) => (
                  <TabStyled
                    key={tab.value}
                    label={tab.label}
                    value={tab.value}
                  />
                ))}
              </StyledTabList>
            </Box>
            <TabPanel value={value}>
              <Outlet />
            </TabPanel>
          </TabContext>
        )}
      </Box>
      {openSnackbar && <Snackbar />}
    </GridMain>
  );
}
